import React, { useMemo } from "react";
import "./CobbleTrail.css";
import { buildStones } from "./cobbleStones";

/**
 * CobbleTrail — the cobblestone path drawn as ONE continuous fitted SVG ribbon.
 *
 * The trail used to be a stack of flat CSS rectangles drawn per category row
 * (.trail-path__bar). This replaces that drawing — but NOT the layout: the same
 * corridor paddings in GameTypesGrid.css still reserve the path's space and inset
 * each category's content, so categories keep alternating left/right as before.
 * Only the FILL changes, from flat bars to a ribbon of whole stones.
 *
 * The shared serpentine (centerline + segment index) is measured once by
 * useTrailGeometry and handed in as `geometry`; the stone placement lives in
 * cobbleStones.js (pure, unit-tested). This component just renders it.
 */
export default function CobbleTrail({ geometry }) {
  const tonePaths = useMemo(() => (geometry ? buildStones(geometry) : null), [geometry]);
  if (!geometry || !tonePaths) return null;

  const { width, minY, maxY } = geometry;
  const height = maxY - minY;
  return (
    <svg
      className="cobble-trail"
      width={width}
      height={height}
      viewBox={`0 ${minY} ${width} ${height}`}
      style={{ top: `${minY}px` }}
      aria-hidden="true"
    >
      {/* No mortar bed: the stones sit straight on the paper, with the meadow
          showing in the gaps between them — there's no grey slab behind the path. */}
      <g className="cobble-trail__stones">
        {tonePaths.map((d, tone) =>
          d ? (
            <path
              key={tone}
              className={`cobble-trail__stone cobble-trail__stone--${tone}`}
              d={d}
            />
          ) : null
        )}
      </g>
    </svg>
  );
}
